import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTabGroup } from '@angular/material/tabs';

import { Store } from '@ngrx/store';

import { Observable, Subscription } from 'rxjs';

import { ToastrService } from 'ngx-toastr';

import { StudyType } from '../../../../models/study-type';
import { Language } from '../../../../models/language';
import { AppState } from '../../../../state';
import {
  selectIsLoadingSelectedStudy,
  selectSelectedLanguage,
  selectSelectedStudy,
  selectSelectedStudyLoaded,
  selectSupportedLanguages,
  selectUploadStudyImage,
} from '../../../state/study/study.selectors';
import {
  addStudySupportedLanguages,
  createStudy,
  setSelectedLanguage,
  updateStudy,
} from '../../../state/study/study.actions';
import { dateLessThan, removeWhitespaces } from '../../../../core/custom-form-validators';
import * as moment from 'moment';
import { Study } from '../../../../models/study';
import { setLanguageTabIndex } from 'src/app/core/languages-helper';
import {
  API_DATE_FORMAT,
  LANGUAGES,
  PRIVILEGE_ACTIONS,
  PRIVILEGE_SECTIONS,
} from '../../../../core/dashboard-constants';
import { UploadIds, UploadKeys } from 'src/app/models/upload';
import { PrivilegeService } from '../../../services/privilege/privilege.service';
import { isThereEmptyTranslation } from '../../helpers/translation-helper';
import { Role } from '../../../../models/role';
import { initializeUpload, uploadRequest } from '../../../state/upload/upload.actions';
import { selectUploaded } from '../../../state/upload/upload.selectors';
import { v4 as uuidv4 } from 'uuid';
import { DASHBOARD_ROUTES } from '../../../dashboard.routes';
import { STUDIES_ROUTES } from '../../studies.routes';
import * as studyActions from '../../../state/study/study.actions';

export const STUDY_TITLE_IDENTIFIER = 'title';
export const STUDY_DESCRIPTION_IDENTIFIER = 'description';

@Component({
  selector: 'dashboard-create-study',
  templateUrl: './study-details.component.html',
  styleUrls: ['./study-details.component.scss'],
})
export class StudyDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('studyStartsPicker') studyStartsPicker: any;
  @ViewChild('studyEndsPicker') studyEndsPicker: any;

  studyDetailsForm: FormGroup;

  StudyType = StudyType;
  selectedStudyType: StudyType;

  now = moment(new Date());

  @ViewChild('languageTabs', { static: false }) languageTabs: MatTabGroup;
  supportedLanguages$: Observable<Language[]>;
  supportedLanguages: Language[];
  selectedLanguage$: Observable<Language>;
  selectedLanguage: Language;
  selectedLanguageDropdown: Language;
  languagesArray = LANGUAGES;

  mainLanguagesArray = this.languagesArray.slice(0, 3);
  otherLanguagesArray = this.languagesArray.slice(3, 6);

  isEdit: boolean;
  isImageToEdit: boolean;

  imgPath: string;
  idsForUpload: UploadIds;
  keyForUpload = UploadKeys.UPLOAD_FROM_STUDY;

  loadingSelectedStudy$: Observable<boolean>;
  selectedStudyLoaded$: Observable<boolean>;
  selectedStudy$: Observable<Study>;
  selectedStudy: Study;

  uploadedFiles$: Observable<File[] | string[]>;
  uuid = uuidv4();

  uploadImageSuccess$: Observable<any>;

  PRIVILEGE_SECTIONS = PRIVILEGE_SECTIONS;
  PRIVILEGE_ACTIONS = PRIVILEGE_ACTIONS;

  Role = Role;

  private subscriptions$ = new Subscription();
  private details: any;

  constructor(
    private formBuilder: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private store: Store<AppState>,
    private toastrService: ToastrService,
    public privilegeService: PrivilegeService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.studyDetailsForm = this.formBuilder.group(
      {
        studyTranslations: this.formBuilder.array([]),
        imagePath: [''],
        startsAt: [this.now, [Validators.required]],
        endsAt: [moment(new Date()).add(1, 'day'), [Validators.required]],
        color: [''],
        healthEnabled: [false],
        phenotypingEnabled: [false],
        emailRequired: [false],
        feedback: [false],
      },
      { validator: dateLessThan('startsAt', 'endsAt') }
    );

    this.supportedLanguages$ = this.store.select(selectSupportedLanguages);
    this.subscriptions$.add(this.supportedLanguages$.subscribe((languages) => (this.supportedLanguages = languages)));

    this.selectedLanguage$ = this.store.select(selectSelectedLanguage);
    this.subscriptions$.add(this.selectedLanguage$.subscribe((language) => (this.selectedLanguage = language)));

    this.uploadImageSuccess$ = this.store.select(selectUploadStudyImage);
    this.subscriptions$.add(
      this.uploadImageSuccess$.subscribe((uploadedComplete) => {
        if (uploadedComplete) {
          const newStudy = JSON.parse(sessionStorage.getItem('newStudy'));
          this.router.navigate([`${DASHBOARD_ROUTES.STUDIES}/${newStudy.id}/${STUDIES_ROUTES.HOME}`]).then(() => {
            window.location.reload();
          });
          studyActions.openSideNav({ navType: newStudy.type });
        }
      })
    );

    this.store.dispatch(initializeUpload({ uuid: this.uuid }));

    this.uploadedFiles$ = this.store.select(selectUploaded(this.uuid));
    this.subscriptions$.add(
      this.uploadedFiles$.subscribe((uploaded) => {
        if (uploaded && uploaded.length > 0 && !this.isEdit) {
          const newStudy = JSON.parse(sessionStorage.getItem('newStudy'));
          newStudy.imagePath = uploaded[0];
          newStudy.languages = newStudy.languages.toString();
          this.store.dispatch(updateStudy({ study: newStudy, skipFlash: true }));
          this.toastrService.success(`Successfully created study ${newStudy.translations[0].translation}`);
        } else if (uploaded && uploaded.length > 0 && this.isEdit) {
          this.details.imagePath = uploaded[0];
          this.store.dispatch(updateStudy({ study: this.details }));
        }
      })
    );

    if (this.activatedRoute.snapshot?.queryParams?.type) {
      this.selectedStudyType = this.activatedRoute.snapshot.queryParams.type;
    } else {
      this.isEdit = true;
    }

    if (this.isEdit) {
      this.loadingSelectedStudy$ = this.store.select(selectIsLoadingSelectedStudy);
      this.selectedStudyLoaded$ = this.store.select(selectSelectedStudyLoaded);
      this.selectedStudy$ = this.store.select(selectSelectedStudy);
      this.subscriptions$.add(
        this.selectedStudy$.subscribe((selectedStudy) => {
          this.selectedStudy = selectedStudy;
          this.idsForUpload = {
            studyId: selectedStudy?.id,
          };
          this.selectedStudyType = this.selectedStudy?.type as StudyType;
          this.removeAllTranslationFields();
          if (this.selectedStudy) {
            this.selectedStudy?.languages.forEach((langCode) => this.createTranslationFields(langCode));
            this.setStudyDetailsFormValues();
          }
          if (this.selectedStudy?.imagePath) {
            this.isImageToEdit = true;
            this.imgPath = this.selectedStudy.imagePath;
          }
          this.checkPrivileges();
        })
      );
    } else {
      this.removeAllTranslationFields();
      this.createTranslationFields('en');
      this.checkPrivileges();
    }
  }

  get studyTranslations(): FormArray {
    return this.studyDetailsForm.get('studyTranslations') as FormArray;
  }

  get startsAt(): AbstractControl {
    return this.studyDetailsForm.get('startsAt');
  }

  get endsAt(): AbstractControl {
    return this.studyDetailsForm.get('endsAt');
  }

  get color(): AbstractControl {
    return this.studyDetailsForm.get('color');
  }

  get emailRequired(): AbstractControl {
    return this.studyDetailsForm.get('emailRequired');
  }

  get feedback(): AbstractControl {
    return this.studyDetailsForm.get('feedback');
  }

  get healthEnabled(): AbstractControl {
    return this.studyDetailsForm.get('healthEnabled');
  }

  get phenotypingEnabled(): AbstractControl {
    return this.studyDetailsForm.get('phenotypingEnabled');
  }

  setStudyDetailsFormValues() {
    if (this.selectedStudy?.emailRequired) {
      this.emailRequired.disable();
    }
  }

  checkPrivileges(): void {
    if (
      this.privilegeService.hasPrivilege(PRIVILEGE_SECTIONS.STUDY_DETAILS, PRIVILEGE_ACTIONS.VIEW) &&
      !this.privilegeService.hasPrivilege(PRIVILEGE_SECTIONS.STUDY_DETAILS, PRIVILEGE_ACTIONS.ALL)
    ) {
      this.studyDetailsForm.disable();
    }
  }

  // LANGUAGES

  selectLanguage(chosenLanguage: Language): void {
    this.selectedLanguageDropdown = chosenLanguage;
  }

  addLanguage(): void {
    if (
      this.selectedLanguageDropdown &&
      !this.supportedLanguages.find((lang) => lang.code === this.selectedLanguageDropdown.code)
    ) {
      const newSupportedLanguages = setLanguageTabIndex([...this.supportedLanguages, this.selectedLanguageDropdown]);
      this.store.dispatch(
        addStudySupportedLanguages({
          supportedLanguages: newSupportedLanguages,
        })
      );

      this.createTranslationFields(this.selectedLanguageDropdown.code);

      this.store.dispatch(
        setSelectedLanguage({
          selectedLanguage: {
            ...this.selectedLanguageDropdown,
            tabIndex: this.supportedLanguages.length,
          },
        })
      );
    }
  }

  createTranslationFields(languageCode): void {
    const filteredTranslations = this.selectedStudy?.translations?.filter(
      (translation) => translation.language === languageCode
    );

    const titleTranslation = filteredTranslations?.find(
      (translation) => translation.identifier === STUDY_TITLE_IDENTIFIER
    );
    const descriptionTranslation = filteredTranslations?.find(
      (translation) => translation.identifier === STUDY_DESCRIPTION_IDENTIFIER
    );

    const studyTitle = this.formBuilder.group({
      id: [titleTranslation ? titleTranslation?.id : 0],
      language: [languageCode],
      identifier: [STUDY_TITLE_IDENTIFIER],
      translation: [
        titleTranslation ? titleTranslation?.translation : '',
        [Validators.required, Validators.maxLength(100), removeWhitespaces],
      ],
    });
    const studyDescription = this.formBuilder.group({
      id: [descriptionTranslation ? descriptionTranslation?.id : 0],
      language: [languageCode],
      identifier: [STUDY_DESCRIPTION_IDENTIFIER],
      translation: [
        descriptionTranslation ? descriptionTranslation?.translation : '',
        [Validators.required, removeWhitespaces],
      ],
    });

    this.studyTranslations.push(studyTitle);
    this.studyTranslations.push(studyDescription);
  }

  removeLanguage(selectedLanguage: Language): void {
    const newSupportedLanguages = [...this.supportedLanguages];

    if (newSupportedLanguages.length <= 1) {
      this.toastrService.error('There should be at least one language selected');
      return;
    }

    const indexToRemove = newSupportedLanguages.indexOf(selectedLanguage);
    if (indexToRemove >= 0) {
      newSupportedLanguages.splice(indexToRemove, 1);
    }

    const indexedLanguages = setLanguageTabIndex(newSupportedLanguages);
    this.store.dispatch(addStudySupportedLanguages({ supportedLanguages: indexedLanguages }));

    if (selectedLanguage.code === this.selectedLanguage.code) {
      this.store.dispatch(setSelectedLanguage({ selectedLanguage: this.supportedLanguages[0] }));
    }

    this.studyTranslations.removeAt(
      this.studyTranslations.value.findIndex((translation) => translation.language === selectedLanguage.code)
    );
    this.studyTranslations.removeAt(
      this.studyTranslations.value.findIndex((translation) => translation.language === selectedLanguage.code)
    );

    this.toastrService.warning(
      'In order to delete removed translations permanently, please click on UPDATE STUDY button',
      '',
      { timeOut: 8000 }
    );
  }

  removeAllTranslationFields(): void {
    this.studyTranslations.clear();
  }

  selectTemplateLanguage(event): void {
    const foundSelected = this.supportedLanguages.find((language) => language.name === event.tab.textLabel);
    this.store.dispatch(setSelectedLanguage({ selectedLanguage: foundSelected }));
  }

  // Hack for known issue with mat sidenav and animations for mat tabs
  selectFirstTab() {
    setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
  }

  // COLORS

  setColor(color: string) {
    this.color.setValue(color);
  }

  saveStudy(): void {
    if (isThereEmptyTranslation(this.studyTranslations.value)) {
      this.toastrService.error('Please populate Title and Description in all supported languages');
      return;
    }

    if (this.studyDetailsForm.invalid) {
      return;
    }

    const languageCodes = [];
    this.supportedLanguages.map((lang) => languageCodes.push(lang.code));

    let studyDetails = {
      ...this.studyDetailsForm.value,
      type: this.selectedStudyType,
      startsAt: moment(this.startsAt.value).format(API_DATE_FORMAT),
      endsAt: moment(this.endsAt.value).format(API_DATE_FORMAT),
      languages: languageCodes.toString(),
      healthEnabled: this.selectedStudyType === StudyType.CC ? 0 : +this.healthEnabled.value,
      phenotypingEnabled: this.selectedStudyType === StudyType.CC ? 0 : +this.phenotypingEnabled.value,
      emailRequired: +this.emailRequired.value,
      feedback: +this.feedback.value,
      translations: this.studyTranslations.value,
    };

    delete studyDetails.studyTranslations;

    if (this.selectedStudyType !== StudyType.TIIM) {
      delete studyDetails.imagePath;
    }

    if (this.isEdit) {
      studyDetails = {
        ...studyDetails,
        id: this.selectedStudy.id,
      };
      if (this.studyDetailsForm?.value?.imagePath?.includes('blob:http')) {
        this.uploadLocalFileAndSave(studyDetails);
      } else {
        this.store.dispatch(updateStudy({ study: studyDetails }));
      }
    } else {
      this.store.dispatch(createStudy({ study: studyDetails, uuid: this.uuid }));
    }
  }

  uploadLocalFileAndSave(details) {
    this.details = details;
    this.createBlob().then((file) => {
      this.store.dispatch(
        uploadRequest({
          file,
          uuid: this.uuid,
          keyForUpload: this.keyForUpload,
          idsForUpload: this.idsForUpload,
        })
      );
    });
  }

  createBlob(): Promise<File> {
    return fetch(this.details.imagePath)
      .then((r) => r.blob())
      .then((blobFile) => {
        const type = blobFile.type;
        const extension = '.' + type.split('/')[1];
        return new File([blobFile], this.uuid + extension, { type });
      });
  }

  ngOnDestroy() {
    this.subscriptions$.unsubscribe();
  }
}
